import React, { useState, useEffect } from 'react';
import axios from 'axios';

const COLORS = {
  primary: "#0760ad",
  primaryLight: "#098a48",
  secondary: "#118a94",
  secondaryLight: "#339166",
  white: "#FFFFFF",
};

const DEFAULT_DATA = {
  headerText: "About Warm Wellness Center",
  title: "Compassionate Care, Exceptional Results",
  description: "At WarmWellness Center, we've been redefining healthcare since 2010...",
  values: [
    { title: "Our Mission", text: "To provide healthcare that treats the whole person..." },
    { title: "Our Vision", text: "A community where wellness is accessible to all" },
    { title: "Our Promise", text: "Compassionate, competent care at every visit" }
  ]
};

const About1Modal = ({ isOpen, onClose, initialData }) => {
  const [aboutData, setAboutData] = useState(DEFAULT_DATA);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  useEffect(() => {
    const fetchCurrentData = async () => {
      if (isOpen && !initialData) {
        try {
          setIsLoading(true);
          const response = await fetch(`${import.meta.env.VITE_API_URL}/about/about1/`);
          if (response.ok) {
            const data = await response.json();
            setAboutData(data);
          }
        } catch (error) {
          console.error('Error fetching about data:', error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchCurrentData();
  }, [isOpen, initialData]);

  useEffect(() => {
    if (initialData) {
      setAboutData(initialData);
    }
  }, [initialData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setAboutData(prev => ({ ...prev, [name]: value }));
  };

  const handleValueChange = (index, field, value) => {
    const updatedValues = [...aboutData.values];
    updatedValues[index] = { ...updatedValues[index], [field]: value };
    setAboutData(prev => ({ ...prev, values: updatedValues }));
  };

  const addValue = () => {
    setAboutData(prev => ({
      ...prev,
      values: [...prev.values, { title: "", text: "" }]
    }));
  };

  const removeValue = (index) => {
    const updatedValues = [...aboutData.values];
    updatedValues.splice(index, 1);
    setAboutData(prev => ({ ...prev, values: updatedValues }));
  };

  const handleSubmit = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/about/about1/`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(aboutData)
      });

      if (response.ok) {
        const updatedData = await response.json();
        onClose();
      } else {
        console.error('Failed to update about data');
        alert('Failed to save changes. Please try again.');
      }
    } catch (error) {
      console.error('Error updating about data:', error);
      alert('An error occurred while saving. Please try again.');
    }
  };

  if (isLoading) {
    return (
      <dialog className={`modal ${isOpen ? 'modal-open' : ''}`}>
        <div className="modal-box">
          <span className="loading loading-spinner loading-lg"></span>
          <p>Loading about section data...</p>
        </div>
      </dialog>
    );
  }

  return (
    <dialog className={`modal ${isOpen ? 'modal-open' : ''}`}>
      <div className="modal-box w-11/12 max-w-4xl max-h-[90vh] relative ">
        <button onClick={onClose} className="btn btn-sm btn-circle absolute right-2 top-2">✕</button>
        <h3 className="font-bold text-lg mb-4">Edit About Section</h3>
        
        <div className="overflow-y-auto max-h-[70vh] pr-2 space-y-6">
          <div className="form-control">
            <label className="label">
              <span className="label-text">Header Text</span>
            </label>
            <input 
              type="text" 
              name="headerText"
              value={aboutData.headerText || ''} 
              onChange={handleChange} 
              className="input input-bordered w-full" 
            />
          </div>
          
          <div className="form-control">
            <label className="label">
              <span className="label-text">Title</span>
            </label>
            <input 
              type="text" 
              name="title"
              value={aboutData.title || ''} 
              onChange={handleChange} 
              className="input input-bordered w-full" 
            />
          </div>
          
          <div className="form-control">
            <label className="label">
              <span className="label-text">Description</span>
            </label>
            <textarea 
              name="description"
              value={aboutData.description || ''} 
              onChange={handleChange} 
              className="textarea textarea-bordered w-full h-32" 
            />
          </div>
          
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h4 className="font-bold">Core Values</h4>
              <button onClick={addValue} className="btn btn-sm btn-primary">
                Add Value
              </button>
            </div>
            
            {aboutData.values?.map((value, index) => (
              <div key={index} className="card bg-base-100 shadow-sm p-4 relative">
                <button 
                  onClick={() => removeValue(index)}
                  className="btn btn-sm btn-circle btn-error absolute -top-2 -right-2 z-10"
                >
                  ✕
                </button>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="form-control">
                    <label className="label">
                      <span className="label-text">Title</span>
                    </label>
                    <input 
                      type="text" 
                      value={value.title || ''} 
                      onChange={(e) => handleValueChange(index, 'title', e.target.value)} 
                      className="input input-bordered w-full" 
                    />
                  </div>
                  
                  <div className="form-control">
                    <label className="label">
                      <span className="label-text">Description</span>
                    </label>
                    <input 
                      type="text" 
                      value={value.text || ''} 
                      onChange={(e) => handleValueChange(index, 'text', e.target.value)} 
                      className="input input-bordered w-full" 
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        <div className="modal-action mt-4">
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSubmit}>
            Save Changes
          </button>
        </div>
      </div>
    </dialog>
  );
};

export default About1Modal;